import {getLogger} from './logUtils'

export interface WordPressPost {
  id: number
  date: string
  date_gmt: string
  modified: string
  modified_gmt: string
  slug: string
  status: string
  type: string
  link: string
  title: {
    rendered: string
  }
  content: {
    rendered: string
  }
  excerpt: {
    rendered: string
  }
  featured_media: number
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string
      media_details?: {
        sizes?: {
          medium?: {
            source_url: string
          }
          large?: {
            source_url: string
          }
          'medium_large'?: {
            source_url: string
          }
          full?: {
            source_url: string
          }
        }
      }
    }>
  }
}

export interface BlogPost {
  id: number
  title: string
  subtitle: string
  image: string
  date: string
  link: string
}

const WORDPRESS_API_URL = 'https://www.biobam.com/wp-json/wp/v2/posts'

/**
 * Fetches blog posts from WordPress API
 * @param limit - Maximum number of posts to fetch (default: 4)
 * @returns Array of blog posts
 */
export const fetchBlogPosts = async (limit: number = 4): Promise<BlogPost[]> => {
  try {
    const params = new URLSearchParams({
      per_page: limit.toString(),
      _embed: '1', // Include featured media
      orderby: 'date',
      order: 'desc',
    })

    const response = await fetch(`${WORDPRESS_API_URL}?${params.toString()}`)
    
    if (!response.ok) {
      throw new Error(`WordPress API error: ${response.status} ${response.statusText}`)
    }

    const posts: WordPressPost[] = await response.json()

    return posts.map((post) => {
      // Extract featured image URL
      let imageUrl = 'https://via.placeholder.com/300x200/007bff/ffffff?text=Blog+Post'
      
      if (post._embedded?.['wp:featuredmedia']?.[0]) {
        const media = post._embedded['wp:featuredmedia'][0]
        // Try to get the best available image size
        imageUrl =
          media.media_details?.sizes?.large?.source_url ||
          media.media_details?.sizes?.medium_large?.source_url ||
          media.media_details?.sizes?.medium?.source_url ||
          media.source_url ||
          imageUrl
      }

      // Extract excerpt/subtitle from rendered excerpt (remove HTML tags)
      const excerptText = post.excerpt?.rendered
        ? post.excerpt.rendered
            .replace(/<[^>]*>/g, '') // Remove HTML tags
            .replace(/&[^;]+;/g, '') // Remove HTML entities
            .trim()
            .substring(0, 150) // Limit to 150 characters
        : ''

      // Format date
      const date = new Date(post.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })

      return {
        id: post.id,
        title: post.title.rendered,
        subtitle: excerptText || 'Read more about this post...',
        image: imageUrl,
        date: date,
        link: post.link,
      }
    })
  } catch (error) {
    getLogger().error('Error fetching blog posts:', error)
    throw error
  }
}


